import React from 'react';
import { BarChart3, Users, TrendingUp, Film } from 'lucide-react';
import { VOTE_OPTIONS } from '../../config/supabase';
import MoviePoster from '../viewer/MoviePoster';

const VoteResults = ({ category, movies, voteResults, loading, onMovieClick }) => {
  console.log('🎯 VoteResults component received props:', {
    categoryName: category?.name,
    categoryId: category?.id,
    voteResultsKeys: voteResults ? Object.keys(voteResults) : [],
    voteResultsCount: voteResults ? Object.keys(voteResults).length : 0,
    loading
  });

  const getMovieById = (movieId) => {
    // Handle both string and number IDs by converting both to strings for comparison
    const searchId = String(movieId);
    return movies.find(movie => String(movie.id) === searchId);
  };

  // Parse movie_ids if it's a JSON string, otherwise use as array
  let movieIds = [];
  if (Array.isArray(category.movie_ids)) {
    movieIds = category.movie_ids;
  } else if (typeof category.movie_ids === 'string') {
    try {
      movieIds = JSON.parse(category.movie_ids);
    } catch (error) {
      console.warn('Failed to parse movie_ids JSON string:', error);
      movieIds = [];
    }
  }
  const categoryMovies = movieIds.map(getMovieById).filter(Boolean) || [];
  
  console.log('🎬 VoteResults movie processing:', {
    categoryMovieIds: movieIds,
    categoryMoviesFound: categoryMovies.length,
    categoryMovieNames: categoryMovies.map(m => m?.title)
  });

  if (loading) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto text-orange-400 animate-pulse mb-4" />
        <p className="text-gray-300">Loading vote results...</p>
      </div>
    );
  }

  if (!voteResults || Object.keys(voteResults).length === 0) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-16 h-16 mx-auto text-gray-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-400 mb-2">No Votes Yet</h3>
        <p className="text-gray-500">
          Be the first to vote in this category!
        </p>
      </div>
    );
  }

  // Sort movies by average score (highest first), then by vote count (highest first) for tiebreaking
  // IMPORTANT: Only show results for movies that belong to this category
  const sortedResults = Object.entries(voteResults)
    .map(([movieId, results]) => ({
      movie: getMovieById(movieId),
      movieId,
      ...results
    }))
    .filter(result => result.movie) // Filter out movies that don't exist
    .filter(result => movieIds.includes(result.movieId)) // FILTER: Only movies in this category
    .sort((a, b) => {
      // Primary sort: average score (highest first)
      if (b.averageScore !== a.averageScore) {
        return b.averageScore - a.averageScore;
      }
      // Tiebreaker: total votes (highest first)
      return b.totalVotes - a.totalVotes;
    });
    
  console.log('📊 VoteResults sorted results:', {
    totalResultsFromVoteData: Object.keys(voteResults).length,
    categoryMovieIds: movieIds,
    sortedResultsCount: sortedResults.length,
    sortedMovieTitles: sortedResults.map(r => r.movie?.title),
    filteredCorrectly: sortedResults.length === movieIds.length,
    sampleResult: sortedResults[0]
  });

  const getScoreColor = (score) => {
    if (score >= 2.5) return 'text-red-400'; // I would sell my soul (3 points)
    if (score >= 1.5) return 'text-orange-400'; // I want to watch (2 points)
    if (score >= 0.5) return 'text-yellow-400'; // I'd watch it (1 point)
    if (score >= -0.5) return 'text-gray-400'; // Ehhhh (0 points)
    return 'text-red-600'; // No thanks (-1 points)
  };

  const getScoreEmoji = (score) => {
    if (score >= 2.5) return '👹'; // I would sell my soul (3 points)
    if (score >= 1.5) return '🔥'; // I want to watch (2 points)
    if (score >= 0.5) return '👍'; // I'd watch it (1 point)
    if (score >= -0.5) return '😐'; // Ehhhh (0 points)
    return '👎'; // No thanks (-1 points)
  };

  const totalVotes = sortedResults.reduce((sum, result) => sum + result.totalVotes, 0);

  return (
    <div className="space-y-6">
      {/* Results Summary */}
      <div className="bg-gray-700 border border-orange-500 rounded-lg p-6">
        <h3 className="text-orange-300 font-semibold mb-4 flex items-center gap-2">
          <BarChart3 size={20} />
          Results Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">{totalVotes}</div>
            <div className="text-gray-300 text-sm">Total Votes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">{sortedResults.length}</div>
            <div className="text-gray-300 text-sm">Movies Rated</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">
              {sortedResults.length > 0 ? (totalVotes / sortedResults.length).toFixed(1) : '0'}
            </div>
            <div className="text-gray-300 text-sm">Avg Votes/Movie</div>
          </div>
        </div>
      </div>

      {/* Top Movies */}
      {sortedResults.length > 0 && (
        <div className="bg-gray-700 border border-orange-500 rounded-lg p-6">
          <h3 className="text-orange-300 font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={20} />
            Top Rated Movies
          </h3>
          <div className="space-y-3">
            {sortedResults.slice(0, 3).map((result, index) => (
              <div 
                key={result.movieId} 
                className="flex items-center gap-4 p-3 bg-gray-600 rounded-lg cursor-pointer hover:bg-gray-500 transition-all duration-200 transform hover:scale-[1.02]"
                onClick={() => onMovieClick && onMovieClick(result.movie)}
                title="Click to view movie details"
              >
                <div className={`text-2xl font-bold ${
                  index === 0 ? 'text-yellow-400' : 
                  index === 1 ? 'text-gray-300' : 
                  'text-orange-600'
                }`}>
                  #{index + 1}
                </div>
                <div className="w-12 h-16 flex-shrink-0">
                  <MoviePoster movie={result.movie} size="sm" className="w-full h-full object-cover rounded" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-orange-200 group-hover:text-orange-100">{result.movie.title}</div>
                  <div className="text-gray-400 text-sm">{result.movie.year}</div>
                </div>
                <div className="text-right">
                  <div className={`text-xl font-bold ${getScoreColor(result.averageScore)}`}>
                    {getScoreEmoji(result.averageScore)} {result.averageScore.toFixed(1)}
                  </div>
                  <div className="text-gray-400 text-sm">{result.totalVotes} votes</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Results */}
      <div className="space-y-4">
        <h3 className="text-orange-300 font-semibold text-lg flex items-center gap-2">
          <Film size={20} />
          All Results
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedResults.map((result, index) => (
            <div 
              key={result.movieId} 
              className="bg-gray-700 border border-orange-500 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-600 hover:border-orange-400 transition-all duration-200 transform hover:scale-[1.02]"
              onClick={() => onMovieClick && onMovieClick(result.movie)}
              title="Click to view movie details"
            >
              {/* Movie Header */}
              <div className="relative">
                <MoviePoster
                  movie={result.movie}
                  size="md"
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-white group-hover:text-orange-200">{result.movie.title}</h4>
                      <p className="text-gray-300 text-sm">{result.movie.year}</p>
                    </div>
                    <div className="text-right">
                      <div className={`text-xl font-bold ${getScoreColor(result.averageScore)}`}>
                        {getScoreEmoji(result.averageScore)} {result.averageScore.toFixed(1)}
                      </div>
                      <div className="text-gray-300 text-sm">#{index + 1}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vote Breakdown */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-orange-300 font-semibold text-sm">Vote Breakdown:</span>
                  <span className="text-gray-400 text-sm flex items-center gap-1">
                    <Users size={12} />
                    {result.totalVotes} votes
                  </span>
                </div>
                
                <div className="space-y-2">
                  {VOTE_OPTIONS.map(option => {
                    const count = result.distribution[option.value] || 0;
                    const percentage = result.totalVotes > 0 ? (count / result.totalVotes) * 100 : 0;
                    
                    return (
                      <div key={option.value} className="flex items-center gap-2">
                        <span className="text-sm">{option.emoji}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-gray-300 text-xs">{option.label}</span>
                            <span className="text-gray-400 text-xs">{count}</span>
                          </div>
                          <div className="w-full bg-gray-600 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                option.value === 5 ? 'bg-red-500' :
                                option.value === 4 ? 'bg-orange-500' :
                                option.value === 3 ? 'bg-yellow-500' :
                                option.value === 2 ? 'bg-gray-500' :
                                'bg-red-700'
                              }`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-gray-400 text-xs w-12 text-right">
                          {percentage.toFixed(0)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VoteResults;
